// Login Page Component
// Save as: ~/cfo-dashboard/frontend/src/components/Login.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Pull active users so the quick-fill buttons reflect real accounts.
  useEffect(() => {
    axios.get('/api/auth/login-emails')
      .then((r) => setAccounts(r.data.users || []))
      .catch(() => setAccounts([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const quickLogin = (userEmail) => {
    setEmail(userEmail);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-icon">RM</div>
          <h1>RunningMen <span className="accent">4D</span></h1>
          <p>Sign in to the dashboard</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@runningmen.my"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {accounts.length > 0 && (
          <div className="quick-login">
            <p className="quick-login-title">Fill email</p>
            {(() => {
              const GROUPS = [
                { key: 'sales', label: 'Sales', roles: ['salesperson'] },
                { key: 'admin', label: 'Admin', roles: ['admin'] },
                { key: 'super', label: 'Superadmin', roles: ['superadmin'] },
              ];
              const known = GROUPS.flatMap((g) => g.roles);
              const groups = GROUPS
                .map((g) => ({ ...g, members: accounts.filter((a) => g.roles.includes(a.role)) }))
                .filter((g) => g.members.length > 0);
              // Any account whose role isn't one of the known ones still shows up.
              const others = accounts.filter((a) => !known.includes(a.role));
              if (others.length > 0) groups.push({ key: 'other', label: 'Other', members: others });

              return groups.map((g) => (
                <div className={`quick-group qg-${g.key}`} key={g.key}>
                  <div className="quick-group-label">
                    <span className="quick-dot" />
                    <span className="quick-group-name">{g.label}</span>
                    <span className="quick-group-count">{g.members.length}</span>
                  </div>
                  <div className="quick-grid">
                    {g.members.map((a) => (
                      <button
                        key={a.email}
                        type="button"
                        className={`quick-btn qb-${g.key}${email === a.email ? ' active' : ''}`}
                        onClick={() => quickLogin(a.email)}
                        title={a.email}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
